import { useEffect, useState, type FC, type FormEvent } from "react";
import { Button, formatCurrency, Modal } from "../../_common";
import { useApplyManualPrice, usePreviewManualPrice, type ManualPricePreview } from "../../symbol";

export interface ManualPriceTarget {
  id: string;
  ticker: string;
  price: number | null;
  priceDate: string | null;
  quantity: number;
  currency: string;
}

export interface ManualPriceModalProps {
  portfolioId: string;
  target: ManualPriceTarget | null;
  onClose: () => void;
}

const inputClass = "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";

export const ManualPriceModal: FC<ManualPriceModalProps> = ({ portfolioId, target, onClose }) => {
  const previewPrice = usePreviewManualPrice();
  const applyPrice = useApplyManualPrice(portfolioId);
  const [price, setPrice] = useState("");
  const [priceDate, setPriceDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState<ManualPricePreview | null>(null);

  useEffect(() => {
    if (!target) return;
    setPrice(target.price === null ? "" : String(target.price));
    setPriceDate(target.priceDate ?? new Date().toISOString().slice(0, 10));
    setReason("");
    setShowPreview(false);
    setPreview(null);
    previewPrice.reset();
    applyPrice.reset();
  }, [target]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!target || !reason.trim()) return;
    if (!showPreview) {
      try {
        const result = await previewPrice.mutateAsync({
          id: target.id, portfolioId, price: Number(price), priceDate, reason: reason.trim(),
        });
        setPreview(result);
        setShowPreview(true);
      } catch {
        setPreview(null);
      }
      return;
    }
    await handleApply();
  };

  const handleApply = async () => {
    if (!target || !reason.trim()) return;
    try {
      if (!preview?.canApply) return;
      await applyPrice.mutateAsync({
        id: target.id,
        portfolioId,
        price: Number(price),
        priceDate,
        reason: reason.trim(),
        expectedMarketPriceRevision: preview.expectedMarketPriceRevision,
        expectedIdentityFingerprint: preview.expectedIdentityFingerprint,
        affectedPortfolioIds: preview.affectedPortfolioIds,
      });
      onClose();
    } catch {
      // The mutation error is shown in the form.
    }
  };

  return (
    <Modal open={target !== null} onClose={onClose} title={`Set manual price · ${target?.ticker ?? ""}`}>
      <p className="mb-3 text-sm text-muted-foreground">
        Saving switches this security to manual valuation. The price affects every portfolio that holds it from the date below. Use Return to FMP when you want to restore automatic prices.
      </p>
      {showPreview && target ? (
        <div className="flex flex-col gap-3">
          <h3 className="font-medium">Review the change</h3>
          <p className="text-sm">
            {target.ticker}: {preview?.symbol.price === null ? "No current market value" : formatCurrency((preview?.symbol.price ?? target.price ?? 0) * target.quantity, target.currency)}
            {" → "}{formatCurrency(Number(price) * target.quantity, target.currency)}
          </p>
          <p className="text-sm text-muted-foreground">
            {target.quantity} shares × {price} {target.currency}; applies from {priceDate}. This affects {preview?.affectedPortfolioIds.length ?? 1} portfolio(s).
          </p>
          <p className="text-sm">Reason: {reason}</p>
          {preview && !preview.canApply && <p role="alert" className="text-sm text-destructive">{preview.reason}</p>}
          {(previewPrice.error || applyPrice.error) && <p role="alert" className="text-sm text-destructive">{(previewPrice.error ?? applyPrice.error)?.message}</p>}
          <div className="mt-2 flex gap-2">
            <Button type="button" variant="outline" disabled={previewPrice.isPending || applyPrice.isPending} onClick={() => { setShowPreview(false); setPreview(null); }}>Back</Button>
            <Button type="button" disabled={!preview?.canApply || applyPrice.isPending} onClick={() => { void handleApply(); }}>
              {applyPrice.isPending ? "Saving..." : "Apply manual price"}
            </Button>
          </div>
        </div>
      ) : (
      <form onSubmit={(event) => { void handleSubmit(event); }} className="flex flex-col gap-3">
        <label className="text-sm font-medium" htmlFor="manual-price-value">Price</label>
        <input
          id="manual-price-value"
          aria-label="Manual price"
          className={inputClass}
          type="number"
          step="any"
          min="0"
          required
          value={price}
          onChange={(event) => setPrice(event.target.value)}
        />
        <label className="text-sm font-medium" htmlFor="manual-price-date">Price date</label>
        <input
          id="manual-price-date"
          aria-label="Manual price date"
          className={inputClass}
          type="date"
          required
          value={priceDate}
          onChange={(event) => setPriceDate(event.target.value)}
        />
        <label className="text-sm font-medium" htmlFor="manual-price-reason">Why are you entering this price?</label>
        <textarea
          id="manual-price-reason"
          aria-label="Manual price reason"
          className="min-h-20 rounded-md border border-input bg-background px-3 py-2 text-sm"
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {(previewPrice.error || applyPrice.error) && <p role="alert" className="text-sm text-destructive">{(previewPrice.error ?? applyPrice.error)?.message}</p>}
        <div className="mt-2 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={previewPrice.isPending || !reason.trim()}>
            {previewPrice.isPending ? "Checking..." : "Review price change"}
          </Button>
        </div>
      </form>
      )}
    </Modal>
  );
};
